import { useEffect, useState } from 'react';
import { teamAPI, announcementsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Users, UserPlus, Trash2, RefreshCw, Megaphone, Plus, Shield, Edit, Save
} from 'lucide-react';

export const TeamManagementPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hierarchies, setHierarchies] = useState([]);
  const [users, setUsers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingHierarchy, setEditingHierarchy] = useState(null);
  const [announcementDialog, setAnnouncementDialog] = useState(false);
  const [newHierarchy, setNewHierarchy] = useState({ manager_id: '', agent_id: '', team_name: '' });
  const [editHierarchy, setEditHierarchy] = useState({ manager_id: '', agent_id: '', team_name: '' });
  const [newAnnouncement, setNewAnnouncement] = useState({ 
    title: '', content: '', priority: 'normal', target_roles: [], is_pinned: false 
  });

  const roles = ['admin', 'sales_rep', 'marketing_manager', 'support_agent', 'technical_support'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [hierarchyRes, usersRes, annRes] = await Promise.all([
        teamAPI.getHierarchy(),
        teamAPI.getUsers(),
        announcementsAPI.getAll()
      ]);
      setHierarchies(hierarchyRes.data);
      setUsers(usersRes.data);
      setAnnouncements(annRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (hierarchy) => {
    setEditingHierarchy(hierarchy);
    setEditHierarchy({
      manager_id: hierarchy.manager_id || '',
      agent_id: hierarchy.agent_id || '',
      team_name: hierarchy.team_name || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateHierarchy = async () => {
    if (!editingHierarchy) return;
    
    try {
      await teamAPI.updateHierarchy(editingHierarchy.id, editHierarchy);
      toast.success('Team assignment updated');
      setEditDialogOpen(false);
      setEditingHierarchy(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update assignment');
    }
  };

  const handleCreateHierarchy = async () => {
    if (!newHierarchy.manager_id || !newHierarchy.agent_id) {
      toast.error('Please select both manager and agent');
      return;
    }
    if (newHierarchy.manager_id === newHierarchy.agent_id) {
      toast.error('Manager and agent cannot be the same person');
      return;
    }
    try {
      await teamAPI.createHierarchy(newHierarchy);
      toast.success('Team assignment created');
      setDialogOpen(false);
      setNewHierarchy({ manager_id: '', agent_id: '', team_name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleRemoveHierarchy = async (hierarchyId) => {
    if (!confirm('Remove this team assignment?')) return;
    try {
      await teamAPI.removeHierarchy(hierarchyId);
      toast.success('Assignment removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.content) {
      toast.error('Please enter title and content');
      return;
    }
    try {
      await announcementsAPI.create(newAnnouncement);
      toast.success('Announcement posted');
      setAnnouncementDialog(false);
      setNewAnnouncement({ title: '', content: '', priority: 'normal', target_roles: [], is_pinned: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to post announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!confirm('Delete this announcement?')) return;
    try {
      await announcementsAPI.delete(id);
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleRole = (role) => {
    setNewAnnouncement(prev => {
      const current = prev.target_roles || [];
      const updated = current.includes(role)
        ? current.filter(r => r !== role)
        : [...current, role];
      return { ...prev, target_roles: updated };
    });
  };

  // Check admin access
  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">Only administrators can access team management.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-management-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Team Management</h1>
          <p className="text-muted-foreground mt-1">Manage team hierarchies and announcements</p>
        </div>
      </div>

      <Tabs defaultValue="hierarchy" className="space-y-6">
        <TabsList>
          <TabsTrigger value="hierarchy">
            <Users className="h-4 w-4 mr-2" />
            Team Hierarchy
          </TabsTrigger>
          <TabsTrigger value="announcements">
            <Megaphone className="h-4 w-4 mr-2" />
            Announcements
          </TabsTrigger>
        </TabsList>

        {/* Team Hierarchy Tab */}
        <TabsContent value="hierarchy">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Manager-Agent Assignments</CardTitle>
                <CardDescription>Define who reports to whom for oversight and alerts</CardDescription>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Assignment
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Team Assignment</DialogTitle>
                    <DialogDescription>Assign an agent to report to a manager</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Manager</Label>
                      <Select value={newHierarchy.manager_id} onValueChange={(v) => setNewHierarchy(prev => ({ ...prev, manager_id: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select manager" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => ['admin', 'marketing_manager'].includes(u.role)).map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.first_name} {u.last_name} ({u.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Agent</Label>
                      <Select value={newHierarchy.agent_id} onValueChange={(v) => setNewHierarchy(prev => ({ ...prev, agent_id: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select agent" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.first_name} {u.last_name} ({u.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Team Name (optional)</Label>
                      <Input
                        value={newHierarchy.team_name}
                        onChange={(e) => setNewHierarchy(prev => ({ ...prev, team_name: e.target.value }))}
                        placeholder="e.g., Sales Team A"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleCreateHierarchy}>Create Assignment</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {hierarchies.length > 0 ? (
                <div className="space-y-3">
                  {hierarchies.map(h => (
                    <div key={h.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center font-medium text-blue-700 mx-auto">
                            {h.manager?.first_name?.[0]}{h.manager?.last_name?.[0]}
                          </div>
                          <p className="text-sm font-medium mt-1">{h.manager?.first_name} {h.manager?.last_name}</p>
                          <Badge variant="outline" className="text-xs">{h.manager?.role}</Badge>
                        </div>
                        <div className="text-2xl text-muted-foreground">→</div>
                        <div className="text-center">
                          <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center font-medium mx-auto">
                            {h.agent?.first_name?.[0]}{h.agent?.last_name?.[0]}
                          </div>
                          <p className="text-sm font-medium mt-1">{h.agent?.first_name} {h.agent?.last_name}</p>
                          <Badge variant="outline" className="text-xs">{h.agent?.role}</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {h.team_name && <Badge>{h.team_name}</Badge>}
                        <Button variant="ghost" size="sm" onClick={() => handleOpenEditDialog(h)} data-testid={`edit-hierarchy-${h.id}`}>
                          <Edit className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveHierarchy(h.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No team assignments yet</p>
                  <p className="text-sm">Create assignments to enable manager oversight and alerts</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Hierarchy Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Edit Team Assignment
                </DialogTitle>
                <DialogDescription>
                  Update the manager-agent relationship
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Manager</Label>
                  <Select value={editHierarchy.manager_id} onValueChange={(v) => setEditHierarchy(prev => ({ ...prev, manager_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select manager" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.filter(u => ['admin', 'marketing_manager'].includes(u.role)).map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Agent</Label>
                  <Select value={editHierarchy.agent_id} onValueChange={(v) => setEditHierarchy(prev => ({ ...prev, agent_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Team Name (optional)</Label>
                  <Input
                    value={editHierarchy.team_name}
                    onChange={(e) => setEditHierarchy(prev => ({ ...prev, team_name: e.target.value }))}
                    placeholder="e.g., Sales Team A"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateHierarchy} data-testid="save-hierarchy-btn">
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Announcements Tab */}
        <TabsContent value="announcements">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Company Announcements</CardTitle>
                <CardDescription>Post news and updates for your team</CardDescription>
              </div>
              <Dialog open={announcementDialog} onOpenChange={setAnnouncementDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    New Announcement
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create Announcement</DialogTitle>
                    <DialogDescription>Post a message visible to your team</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        value={newAnnouncement.title}
                        onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Announcement title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Content</Label>
                      <Textarea
                        value={newAnnouncement.content}
                        onChange={(e) => setNewAnnouncement(prev => ({ ...prev, content: e.target.value }))}
                        placeholder="Write your announcement..."
                        rows={4}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select value={newAnnouncement.priority} onValueChange={(v) => setNewAnnouncement(prev => ({ ...prev, priority: v }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center space-x-2 pt-6">
                        <Checkbox
                          checked={newAnnouncement.is_pinned}
                          onCheckedChange={(checked) => setNewAnnouncement(prev => ({ ...prev, is_pinned: checked }))}
                        />
                        <Label>Pin to top</Label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Target Roles (leave empty for all)</Label>
                      <div className="flex flex-wrap gap-2">
                        {roles.map(role => (
                          <Badge
                            key={role}
                            variant={newAnnouncement.target_roles?.includes(role) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => toggleRole(role)}
                          >
                            {role.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleCreateAnnouncement}>Post Announcement</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {announcements.length > 0 ? (
                <div className="space-y-4">
                  {announcements.map(ann => (
                    <div key={ann.id} className={`p-4 border rounded-lg ${ann.is_pinned ? 'bg-amber-50 border-amber-200' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{ann.title}</h3>
                            {ann.is_pinned && <Badge variant="outline" className="text-amber-700">Pinned</Badge>}
                            <Badge variant={
                              ann.priority === 'urgent' ? 'destructive' :
                              ann.priority === 'high' ? 'default' : 'secondary'
                            }>{ann.priority}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-2">{ann.content}</p>
                          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                            <span>By {ann.author?.first_name} {ann.author?.last_name}</span>
                            <span>{format(new Date(ann.created_at), 'MMM d, yyyy h:mm a')}</span>
                            {ann.target_roles?.length > 0 && (
                              <span>To: {ann.target_roles.join(', ')}</span>
                            )}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteAnnouncement(ann.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No announcements yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TeamManagementPage;
